<template>
  <app-launcher
    :avatar="avatar"
    :agent="agent"
    :app-version="appVersion"
    :app-state="appState"
    :actions-type="actionsType"
    :is-initializing="!isReady || isInitializing"
    :is-loading-bundle="isLoadingBundle"
    :is-loading-settings="isLoadingLauncherSettings"
    @go-back="goToEnvRoot"
    @option-click="onOptionClick"
    @sign-out="$emit('sign-out')"
  >
    <component
      :is="component"
      v-if="showAppBundle"
    />

    <app-launcher-content
      v-else
      :app-state="appState"
    />
  </app-launcher>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';

import AppLauncher from '../components/AppLauncher.vue';
import AppLauncherContent from '../components/AppLauncherContent.vue';
import { useAppLauncherIntegration } from '@/composables';
import {
  useAppLauncher,
  useInitializeAppLauncher,
  useAppUpdateTracking,
  useAppLauncherRouting,
} from '../composables';
import { APP_STATES } from '../constants';

/** An entry point to main App Launcher features */
export default defineComponent({
  name: 'AppLauncherContainer',
  components: { AppLauncher, AppLauncherContent },
  props: {
    userId: {
      type: String,
      default: '',
    },
    multiUserId: {
      type: String,
      default: '',
    },
    isReady: Boolean,
  },
  emits: ['sign-out'],
  setup() {
    const {
      avatar,
      agent,
      appState,
      actionsType,
      component,
      settingsInDB,
      isUserAdmin,
      isInitializing,
      isLoadingBundle,
      isLoadingLauncherSettings,
      isUpdateInProgress,
    } = useAppLauncher();

    const { startUpdateTracking, stopUpdateTracking } = useAppUpdateTracking();
    const { initializeAppLauncher } = useInitializeAppLauncher();
    const { goTo, goToAppLauncher, goToEnvRoot, goToAuthUI, onURLChange } = useAppLauncherRouting();
    const { initializeRefs } = useAppLauncherIntegration();

    return {
      avatar,
      agent,
      appState,
      actionsType,
      component,
      settingsInDB,
      /* eslint-disable-next-line no-undef */
      appVersion: HITL_APP_VERSION,
      isUserAdmin,
      isInitializing,
      isLoadingBundle,
      isLoadingLauncherSettings,
      isUpdateInProgress,
      initializeAppLauncher,
      initializeRefs,
      startUpdateTracking,
      onURLChange,
      goTo,
      goToAppLauncher,
      goToEnvRoot,
      goToAuthUI,
      stopUpdateTracking,
    };
  },
  computed: {
    /**
     * We remove app component when user is logged out
     * and then show it again to trigger initialization.
     * TODO: It is possible to manage this inside Agent UI
     */
    showAppBundle() {
      return this.isReady && this.component
        && this.appState === APP_STATES.APP_BUNDLE;
    },
  },
  watch: {
    isReady(isReady, wasReady) {
      if (isReady && !wasReady) {
        this.initialize();
      }
    },
    userId(current, previous) {
      if (current && previous && current !== previous) {
        this.initialize();
      }
    },
    /** TODO: Vue Demi "watch" doesn't work properly with Vue Router 3 */
    $route(current, previous) {
      this.onURLChange(current, previous);
    },
  },
  created() {
    if (this.isReady) {
      this.initialize();
    }
  },
  beforeDestroy() {
    this.stopUpdateTracking();
  },
  methods: {
    async initialize() {
      // Start timing the launcher initialization
      (window as any).globalInitStartTime = performance.now();
      const { userId, multiUserId } = this;

      const skipToAgentUI = this.$route.query.skipToAgentUI === 'true';

      await this.initializeAppLauncher({
        skipToAgentUI: skipToAgentUI,
        userId,
        multiUserId,
        /* Admins can use an option to go back to launcher from apps  */
        onSettingsReadyCallback: () => {
          this.initializeRefs({
            appId: this.settingsInDB.appId,
            launcherAgent: this.agent,
            isUserAdmin: this.isUserAdmin,
            goBackToLauncher: () => this.goToAppLauncher(),
          });
        },
      });

      /* Update status management is a background process */
      const rate = this.isUpdateInProgress
        ? 'update-in-progress'
        : 'background';
      this.startUpdateTracking({ rate });
    },

    onOptionClick(option: string) {
      switch (option) {
        case 'open-profile':
          this.goToAuthUI();
          break;
        default:
          break;
      }
    },
  },
});
</script>
